import { memo, useMemo } from "react"
import { NeoButtonPopup } from "@neo/neo-common-ui/lib"
import { NeoPopupPosition } from "@neo/neo-common-ui/lib/components/neo-popover/NeoButtonPopup"
import PopupContainer from "./PopupContainer"
import ItemBox from "./ItemBox"
import {
  Container,
  TextContainer,
  MoreContainer
} from "../InlineDrawer/StyleComponents"
import TableColumnMoreBtn from "./TableColumnMoreBtn"
import NeoErrorBoundary from "@neo/neo-common-ui/lib/components/neo-error-boundary"
import NeoThemeProvider from "@neo/neo-common-ui/lib/components/neo-theme/NeoThemeProvider"
import { Intl, messages } from "../../forcepoint-platform-utilityui"

export interface PopupComponentProps {
  items: {
    name: string
    color: string
    wrap?: boolean
    hideItems?: boolean
    otherTextStyle?: string
  }[]
  otherTextStyle?: string
}

const POPUP_OFFSET = { distance: 8, skidding: -4 }
const EMPTY_POPUP = <></>

const PopupComponent = ({
  items = [],
  otherTextStyle
}: PopupComponentProps) => {
  // Copy before sorting so we never mutate the caller's array
  const sortedItems = useMemo(
    () =>
      items?.length
        ? [...items].sort((a, b) => (a.name > b.name ? 1 : -1))
        : [],
    [items]
  )
  const topItem = sortedItems[0] || null

  const moreButton = useMemo(
    () => <TableColumnMoreBtn amount={sortedItems.length - 1} />,
    [sortedItems.length]
  )
  const popup = useMemo(
    () =>
      topItem?.hideItems ? EMPTY_POPUP : <PopupContainer items={sortedItems} />,
    [sortedItems, topItem?.hideItems]
  )

  return (
    <NeoErrorBoundary>
      <NeoThemeProvider>
        <Intl.IntlProvider locale={"en"} messages={messages["en"]}>
          <Container>
            {topItem && (
              <>
                <TextContainer>
                  <ItemBox item={topItem} />
                </TextContainer>
                {sortedItems.length > 1 && (
                  <MoreContainer>
                    <NeoButtonPopup
                      buttonComponent={moreButton}
                      popupComponent={popup}
                      popupOffset={POPUP_OFFSET}
                      popupPosition={NeoPopupPosition.BOTTOM_END}
                    />
                  </MoreContainer>
                )}
              </>
            )}
          </Container>
        </Intl.IntlProvider>
      </NeoThemeProvider>
    </NeoErrorBoundary>
  )
}

/**
 * Callers usually build `items` inline, so compare by content rather than by
 * reference or the memo would never hit.
 */
const areEqual = (prev: PopupComponentProps, next: PopupComponentProps) => {
  const a = prev.items
  const b = next.items
  if (a === b) return true
  if (!a || !b || a.length !== b.length) return false
  return a.every(
    (item, i) =>
      item.name === b[i].name &&
      item.color === b[i].color &&
      item.wrap === b[i].wrap &&
      item.hideItems === b[i].hideItems
  )
}

export default memo(PopupComponent, areEqual)
